import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import { ChevronDown, Calculator as CalcIcon, ArrowRight } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { categories, findCategory, type Calculator } from "@/lib/calculators";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/calculators")({
  head: () => ({
    meta: [
      ...routeMeta("/calculators"),
      { title: "Financial Calculators | CCOB And Company PA" },
      { name: "description", content: "Interactive financial calculators for mortgage, retirement, taxes, savings, business break-even and more — built by CCOB And Company PA." },
      { property: "og:title", content: "Financial Calculators — CCOB And Company PA" },
      { property: "og:description", content: "Mortgage, retirement, tax, savings and business calculators in one calm, focused workspace." },
    ],
    links: canonicalLinks("/calculators"),
  }),
  component: CalculatorsPage,
});

const accent = "var(--gold)";
const primaryColor = "var(--primary)";
const borderColor = "var(--border)";
const mutedColor = "var(--muted-foreground)";
const cardColor = "var(--card)";

function CalculatorsPage() {
  const [activeSlug, setActiveSlug] = useState(categories[0].slug);
  const [open, setOpen] = useState(false);
  const category = useMemo(() => findCategory(activeSlug), [activeSlug]);

  return (
    <>
      <PageHero
        eyebrow="Financial Calculators"
        title="Run the numbers with confidence"
        subtitle="Mortgages, retirement, taxes, savings, and business — all in one focused workspace. Adjust the inputs and watch your plan respond in real time."
        image={img.heroResources}
      />

      <section className="py-20 bg-secondary/30">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Sidebar (desktop) + dropdown (mobile) */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                {/* Mobile dropdown */}
                <div className="lg:hidden relative">
                  <button
                    onClick={() => setOpen((o) => !o)}
                    className="w-full flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left"
                  >
                    <span className="flex items-center gap-3">
                      <category.icon className="h-5 w-5 text-gold" />
                      <span className="font-display text-primary">{category.name}</span>
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="absolute z-20 mt-2 w-full rounded-xl border border-border bg-card shadow-xl overflow-hidden">
                      {categories.map((c) => (
                        <button
                          key={c.slug}
                          onClick={() => { setActiveSlug(c.slug); setOpen(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-secondary transition ${
                            c.slug === activeSlug ? "text-gold" : "text-foreground"
                          }`}
                        >
                          <c.icon className="h-4 w-4" />
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Desktop list */}
                <div className="hidden lg:block">
                  <div className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Categories</div>
                  <ul className="space-y-1">
                    {categories.map((c) => {
                      const active = c.slug === activeSlug;
                      return (
                        <li key={c.slug}>
                          <button
                            onClick={() => setActiveSlug(c.slug)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition ${
                              active
                                ? "bg-primary text-primary-foreground"
                                : "text-foreground hover:bg-secondary"
                            }`}
                          >
                            <c.icon className={`h-4 w-4 ${active ? "text-gold" : "text-muted-foreground"}`} />
                            <span className="flex-1">{c.name}</span>
                            <span className="text-[10px] opacity-60">{c.calculators.length}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </aside>

            {/* Main */}
            <div className="lg:col-span-9 space-y-10">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-gold-text">Current category</div>
                <h2 className="mt-2 font-display text-3xl text-primary">{category.name} calculators</h2>
                <p className="mt-3 max-w-2xl text-muted-foreground">Compare scenarios by changing the inputs below. Results are estimates for planning conversations, not tax, legal, or lending advice.</p>
              </div>
              {category.calculators.map((c) => (
                <CalculatorCard key={c.slug} calc={c} categoryKey={category.slug} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-x">
          <div className="rounded-3xl bg-primary text-primary-foreground p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Need help interpreting the numbers?</div>
               <h2 className="font-display text-3xl md:text-4xl">Talk through your plan with a CPA.</h2>
              <div className="gold-line mt-5" />
              <p className="mt-5 opacity-90 max-w-xl">These calculators are a starting point. We'll layer in your full picture — taxes, entity structure, cash flow — and turn the numbers into a real plan.</p>
            </div>
            <div className="md:justify-self-end">
              <Link to="/contact" hash="consultation" className="btn-gold">Schedule consultation <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function CalculatorCard({ calc, categoryKey }: { calc: Calculator; categoryKey: string }) {
  const [values, setValues] = useState<Record<string, number>>(() =>
    Object.fromEntries(calc.fields.map((f) => [f.key, f.defaultValue]))
  );
  const { results, chart } = calc.compute(values);

  return (
    <motion.div
      key={`${categoryKey}-${calc.slug}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="bg-card border border-border rounded-2xl overflow-hidden"
    >
      <div className="p-6 md:p-8 border-b border-border">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold">
          <CalcIcon className="h-3.5 w-3.5" /> Calculator
        </div>
         <h3 className="font-display text-2xl md:text-3xl text-primary mt-2">{calc.title}</h3>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{calc.description}</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-0">
        {/* Inputs */}
        <div className="lg:col-span-2 p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-border">
          <div className="space-y-4">
            {calc.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{f.label}</label>
                <div className="relative">
                  {f.prefix && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{f.prefix}</span>
                  )}
                  <input
                    type="number"
                    value={values[f.key]}
                    step={f.step ?? 1}
                    min={f.min}
                    max={f.max}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: parseFloat(e.target.value) || 0 }))}
                    className={`w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground focus:outline-none focus:border-gold transition ${
                      f.prefix ? "pl-7" : ""
                    } ${f.suffix ? "pr-12" : ""}`}
                  />
                  {f.suffix && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{f.suffix}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-3 p-6 md:p-8 bg-secondary/30">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Results</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {results.map((r) => (
              <div
                key={r.label}
                className={`rounded-xl p-4 border ${
                  r.highlight
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border"
                }`}
              >
                <div className={`text-[11px] uppercase tracking-wider ${r.highlight ? "text-gold" : "text-muted-foreground"}`}>
                  {r.label}
                </div>
                <div className={`font-display mt-1.5 ${r.highlight ? "text-2xl md:text-3xl" : "text-xl text-primary"}`}>
                  {r.value}
                </div>
              </div>
            ))}
          </div>

          {chart && chart.data.length > 0 && (
            <div className="mt-6 h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                {chart.data.length <= 5 ? (
                  <BarChart data={chart.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Bar dataKey={chart.key1} fill={accent} radius={[6, 6, 0, 0]} />
                  </BarChart>
                ) : (
                  <AreaChart data={chart.data}>
                    <defs>
                      <linearGradient id={`g-${calc.slug}-1`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={accent} stopOpacity={0.5} />
                        <stop offset="100%" stopColor={accent} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id={`g-${calc.slug}-2`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Area type="monotone" dataKey={chart.key1} stroke={accent} fill={`url(#g-${calc.slug}-1)`} strokeWidth={2} />
                    {chart.key2 && (
                      <Area type="monotone" dataKey={chart.key2} stroke="hsl(var(--primary))" fill={`url(#g-${calc.slug}-2)`} strokeWidth={2} />
                    )}
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
